<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[70] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="close"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4">
        <div class="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <header class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ form.id ? '编辑财富客户' : '新建财富客户' }}</h3>
              <p class="mt-1 text-sm text-slate-500">客户证件唯一性和数据权限由后端校验。</p>
            </div>
            <button type="button" class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" @click="close">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto p-6">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label class="block">
                <span class="form-label">客户姓名 <span class="text-red-500">*</span></span>
                <input v-model="form.name" class="form-input w-full" placeholder="请输入客户姓名">
              </label>
              <label class="block">
                <span class="form-label">手机号</span>
                <input v-model="form.mobile" class="form-input w-full" placeholder="请输入手机号">
              </label>
              <label class="block">
                <span class="form-label">证件类型</span>
                <select v-model="form.idType" class="form-select-native w-full">
                  <option value="">请选择</option>
                  <option v-for="item in idTypeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
                </select>
              </label>
              <label class="block">
                <span class="form-label">证件号</span>
                <input v-model="form.idNo" class="form-input w-full" placeholder="请输入证件号">
              </label>
              <label class="block">
                <span class="form-label">性别</span>
                <select v-model="form.gender" class="form-select-native w-full">
                  <option value="">请选择</option>
                  <option v-for="item in genderOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
                </select>
              </label>
              <label class="block">
                <span class="form-label">出生日期</span>
                <input v-model="form.birthday" type="date" class="form-input w-full">
              </label>
              <label class="block">
                <span class="form-label">风险等级</span>
                <select v-model="form.riskLevel" class="form-select-native w-full">
                  <option value="">请选择</option>
                  <option v-for="item in riskLevelOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
                </select>
              </label>
              <div v-if="hasOriginalLinkedCrmCustomer" class="block">
                <span class="form-label">关联 CRM 客户</span>
                <div class="crm-customer-readonly form-readonly w-full">
                  {{ linkedCrmCustomerDisplay }}
                </div>
              </div>
              <div v-else class="block">
                <span class="form-label">关联 CRM 客户</span>
                <el-select
                  v-model="form.linkedCrmCustomerId"
                  filterable
                  remote
                  reserve-keyword
                  clearable
                  class="w-full"
                  placeholder="搜索客户姓名、手机号或企业名称"
                  :remote-method="handleCrmCustomerSearch"
                  :loading="crmCustomerLoading"
                  @visible-change="handleCrmCustomerDropdownVisible"
                >
                  <el-option
                    v-for="customer in crmCustomerList"
                    :key="customer.id"
                    :label="formatCrmCustomerLabel(customer)"
                    :value="customer.id"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <span class="truncate text-sm text-slate-800">{{ customer.name || '--' }}</span>
                      <span class="shrink-0 text-xs text-slate-400">{{ customer.phone || customer.customerNo || customer.id }}</span>
                    </div>
                  </el-option>
                </el-select>
              </div>
              <label class="block">
                <span class="form-label">紧急联系人</span>
                <input v-model="form.emergencyContactName" class="form-input w-full" placeholder="联系人姓名">
              </label>
              <label class="block">
                <span class="form-label">紧急联系人手机号</span>
                <input v-model="form.emergencyContactMobile" class="form-input w-full" placeholder="联系人手机号">
              </label>
              <label class="block md:col-span-2">
                <span class="form-label">地址</span>
                <input v-model="form.address" class="form-input w-full" placeholder="请输入地址">
              </label>
              <label class="block md:col-span-2">
                <span class="form-label">备注</span>
                <textarea v-model="form.remark" rows="3" class="form-textarea w-full" placeholder="请输入备注"></textarea>
              </label>
            </div>
          </main>

          <footer class="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button type="button" class="btn-secondary" @click="close">取消</button>
            <button type="button" class="btn-primary" :disabled="submitting" @click="submit">保存</button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getCustomer, getCustomerPage } from '@/api/customer'
import { createWealthCustomer, updateWealthCustomer } from '@/api/wealth'
import { cleanPayload, genderOptions, idTypeOptions, riskLevelOptions } from '../utils'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  customer: {
    type: Object,
    default: null
  },
  submitting: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const form = reactive(defaultForm())
const crmCustomerList = ref([])
const crmCustomerLoading = ref(false)
const linkedCrmCustomerSnapshot = ref(null)
const originalLinkedCrmCustomerId = ref(null)
let crmCustomerSearchTimer = null

const hasOriginalLinkedCrmCustomer = computed(() => Boolean(originalLinkedCrmCustomerId.value))
const linkedCrmCustomerDisplay = computed(() => {
  if (!originalLinkedCrmCustomerId.value) return '未关联'
  const customer = linkedCrmCustomerSnapshot.value || crmCustomerList.value.find(item => item.id === originalLinkedCrmCustomerId.value)
  return customer ? formatCrmCustomerLabel(customer) : `CRM客户 ${originalLinkedCrmCustomerId.value}`
})

function defaultForm() {
  return {
    id: null,
    name: '',
    mobile: '',
    idType: '',
    idNo: '',
    gender: '',
    birthday: '',
    address: '',
    emergencyContactName: '',
    emergencyContactMobile: '',
    riskLevel: 'LOW',
    linkedCrmCustomerId: null,
    remark: ''
  }
}

function reset(value = null) {
  Object.assign(form, defaultForm(), value || {})
  originalLinkedCrmCustomerId.value = value?.linkedCrmCustomerId || null
  linkedCrmCustomerSnapshot.value = null
}

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  if (!form.name?.trim()) {
    ElMessage.warning('请填写客户姓名')
    return
  }
  const payload = cleanPayload({
    name: form.name?.trim(),
    mobile: form.mobile?.trim(),
    idType: form.idType,
    idNo: form.idNo?.trim(),
    gender: form.gender,
    birthday: form.birthday,
    address: form.address?.trim(),
    emergencyContactName: form.emergencyContactName?.trim(),
    emergencyContactMobile: form.emergencyContactMobile?.trim(),
    riskLevel: form.riskLevel,
    remark: form.remark?.trim()
  })
  if (form.id) {
    await updateWealthCustomer(form.id, hasOriginalLinkedCrmCustomer.value
      ? payload
      : cleanPayload({
          ...payload,
          linkedCrmCustomerId: form.linkedCrmCustomerId
        }))
    ElMessage.success('财富客户已更新')
  } else {
    await createWealthCustomer(cleanPayload({
      ...payload,
      linkedCrmCustomerId: form.linkedCrmCustomerId
    }))
    ElMessage.success('财富客户已创建')
  }
  emit('saved')
  close()
}

async function fetchCrmCustomers(keyword = '') {
  crmCustomerLoading.value = true
  try {
    const result = await getCustomerPage(cleanPayload({
      keyword: keyword?.trim(),
      page: 1,
      size: 20
    }))
    const records = result?.records || []
    const selectedId = form.linkedCrmCustomerId
    if (selectedId && !records.some(item => item.id === selectedId)) {
      const selected = crmCustomerList.value.find(item => item.id === selectedId)
      crmCustomerList.value = selected ? [selected, ...records] : records
    } else {
      crmCustomerList.value = records
    }
  } catch (error) {
    console.error('fetchCrmCustomers failed:', error)
    ElMessage.error(error?.message || '获取客户列表失败')
  } finally {
    crmCustomerLoading.value = false
  }
}

function handleCrmCustomerSearch(query) {
  if (crmCustomerSearchTimer) {
    clearTimeout(crmCustomerSearchTimer)
  }
  crmCustomerSearchTimer = setTimeout(() => fetchCrmCustomers(query), 300)
}

function handleCrmCustomerDropdownVisible(visible) {
  if (visible && crmCustomerList.value.length === 0) {
    fetchCrmCustomers('')
  }
}

function formatCrmCustomerLabel(customer = {}) {
  return [customer.name, customer.phone || customer.customerNo].filter(Boolean).join(' / ') || `客户 ${customer.id}`
}

function ensureSelectedCrmCustomer(value) {
  if (value?.linkedCrmCustomerId && !crmCustomerList.value.some(item => item.id === value.linkedCrmCustomerId)) {
    const snapshot = {
      id: value.linkedCrmCustomerId,
      name: value.linkedCrmCustomerName || `CRM客户 ${value.linkedCrmCustomerId}`,
      phone: value.linkedCrmCustomerMobile || ''
    }
    linkedCrmCustomerSnapshot.value = snapshot
    crmCustomerList.value = [snapshot, ...crmCustomerList.value]
  }
}

async function loadLinkedCrmCustomerSnapshot(value) {
  if (!value?.linkedCrmCustomerId) {
    linkedCrmCustomerSnapshot.value = null
    return
  }
  ensureSelectedCrmCustomer(value)
  try {
    const customer = await getCustomer(value.linkedCrmCustomerId)
    if (customer?.id === value.linkedCrmCustomerId) {
      linkedCrmCustomerSnapshot.value = customer
      crmCustomerList.value = [customer, ...crmCustomerList.value.filter(item => item.id !== customer.id)]
    }
  } catch (error) {
    console.error('load linked CRM customer failed:', error)
  }
}

watch(() => props.customer, async value => {
  reset(value)
  await loadLinkedCrmCustomerSnapshot(value)
}, { immediate: true })
watch(() => props.modelValue, async value => {
  if (value) {
    reset(props.customer)
    await loadLinkedCrmCustomerSnapshot(props.customer)
  }
})
</script>
